/**
 * @file   src\components\AppCustomizetheme.tsx
 * @brief  App Customize theme component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { React, useState, Button, Form , useEffect, useParams, useRef} from '../utils/thirdpartyComponents';
import ThemeHelpModal from './ThemeHelpModal';
import HelpIcon from '../assets/images/help-icon.svg';
import CloseIcon from '../assets/images/icons/close';
import Browseimgicon from '../assets/images/browse-file.svg';
import PlusIcon from "../assets/images/plus-icon-grey.svg";
import SplashScreen from './SplashScreen'; 
import HomeScreen from './HomeScreen';
import SubscriptionsScreen from './SubscriptionsScreen';
import { validateImageTypes } from '../utils/helper';
import { PRIMARY_COLOR, SECONDARY_COLOR, BUTTON_PRIMARY_COLOR, BUTTON_TEXT_COLOR, TEXT_PRIMARY_COLOR, CUSTOM_LOGO_IMAGE_SIZE } from '../utils/constants';
import SmallLoader from '../components/SmallLoader';
import { CommonApi, AppsettingshApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { userType } from '../utils/enums';
import { MessageToaster } from "../utils/toastUtil";
import { CommonMessages } from "../messages/validationMessages"

const AppCustomizetheme = ({ formData, setFormData, cuserType, resetstatus, setResetstatus, resetbuttonstatus, setResetbuttonstatus }: { formData: any, setFormData: (data: any) => void; cuserType: string; resetstatus: boolean; setResetstatus: (data: any) => void; resetbuttonstatus: boolean; setResetbuttonstatus: (data: any) => void; }) => {

    const param = useParams();
    const carWashId = param.id ? parseInt(param.id) : 0;
    const [loading, setLoading] = useState(false);
    const toaster = new MessageToaster();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [logoImageError, setLogoImageError] = useState("");
    const [showThemeHelpModal, setShowThemeHelpModal] = useState(false);
    const handleCloseThemeHelpModal = () => setShowThemeHelpModal(false);
    const handleShowThemeHelpModal = () => setShowThemeHelpModal(true);
    const [companyLogoError, setCompanyLogoError] = useState("");
    const [companyLogoUrl, setcompanyLogoUrl] = useState("");
    const [companyLogo, setCompanyLogo] = useState("");
    const [companyLogoName, setcompanyLogoName] = useState("");
    const [themeData, setThemeData] = useState({
        PrimaryColor: PRIMARY_COLOR,
        SecondaryColor: SECONDARY_COLOR,
        ButtonPrimaryColor: BUTTON_PRIMARY_COLOR,
        ButtonTextColor: BUTTON_TEXT_COLOR,
        TextPrimaryColor: TEXT_PRIMARY_COLOR
    });

    const ColorChangeHandler = (e:any) => {
        setResetbuttonstatus(true);
        e.target.parentElement.style.background = e.target.value;
        e.target.parentElement.children[0].style.display = "none";
        document.documentElement.style.setProperty('--'+e.target.name, e.target.value);
        setThemeData({
            ...themeData,
            [e.target.name]: e.target.value,
        });
        
    }

    //Upload Image
    const uploadCompanyLogo = async (e: any) => {
        setLogoImageError('');
        if (e.target.files && e.target.files[0]) {
            const img = e.target.files[0];
            const fileSizeMB = (img.size / 1048576).toFixed(2);
            if(parseFloat(fileSizeMB) > CUSTOM_LOGO_IMAGE_SIZE) {
                setLoading(false)
                setLogoImageError(CommonMessages.custom_logo_image_size_validation);
                return false;
            }
            const isValid = validateImageTypes(img);
            if (isValid) {
                const folderName = "logo/"
                const randomNumber = Math.floor(Math.random() * 1000000);
                const extension = img.name.substring(img.name.lastIndexOf("."));
                const newImageName = randomNumber + extension;
                setLoading(true)
                setCompanyLogo(URL.createObjectURL(img));
                setcompanyLogoName(img.name)
                const request_params = {
                    key: folderName + newImageName
                }

                const response = (cuserType === userType['CARWASH-OWNER']) ? 
                await axios.post(CommonApi.CARWASH_OWNER_PRESIGNED_URL, request_params) :
                await axios.post(CommonApi.PRESIGNED_URL, request_params) ;
                if (response?.data?.error === 0) {
                    const uploadURL = response.data.data
                    if (uploadURL) {
                        try {
                            // PUT request: upload file to S3
                            const result = await fetch(uploadURL, {
                                method: "PUT",
                                body: img,
                            });
                            if (result.status === 200) {
                                setResetbuttonstatus(true);
                                setLoading(false);
                                setcompanyLogoUrl(folderName + newImageName);
                                if(cuserType === userType['CARWASH-OWNER'])
                                {
                                    setFormData({
                                        ...formData,
                                        ['logoUrl']: folderName + newImageName,
                                        IsCustom: 1
                                    }); 
                                }
                                else
                                {
                                    setFormData({
                                        ...formData,
                                        ['logoUrl']: folderName + newImageName,
                                        IsCustom: 1,
                                        carWashId: carWashId
                                    });
                                }
                                setcompanyLogoUrl(folderName + newImageName);
                                
                            } else {
                                setLoading(false)
                            }
                        } catch (e) {
                            setLoading(false)
                            console.log(e)
                        }
                    } else {
                        setLoading(false)
                    }
                } else {
                    setLoading(false)
                    setLogoImageError(response?.data?.message)
                }
            } else {
                setLogoImageError("Upload image files with .png or .jpeg extensions only.")
                setCompanyLogo('');
                setcompanyLogoName('')
            }
        }
    }

    const removeCompanyLogo = async () => {
        setcompanyLogoUrl('');
        setCompanyLogo('');
        setcompanyLogoName('');
        formData.logoUrl="";
        // Reset the file input value to allow re-uploading the same file
        if (fileInputRef.current) {
            fileInputRef.current.value = ''; // Setting to an empty string clears the input
        }
    }

    useEffect(() => {
        if(resetstatus === true)
        {
            resetCustomTheme();
        }
    }, [resetstatus]);

    const resetCustomTheme = async () => {
        removeCompanyLogo();
        getAppSettings();
        setResetstatus(false);
        setResetbuttonstatus(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    useEffect(() => {
        if(cuserType === userType['CARWASH-OWNER'])
        {
            setFormData({
                appTheme: {
                    primary: themeData.PrimaryColor,
                    secondary: themeData.SecondaryColor,
                    btn_primary: themeData.ButtonPrimaryColor,
                    btn_secondary: themeData.ButtonTextColor,
                    text_primary: themeData.TextPrimaryColor
                },
                logoUrl: companyLogoUrl,
                IsCustom: 1
            });
        }
        else
        {
            setFormData({
                appTheme: {
                    primary: themeData.PrimaryColor,
                    secondary: themeData.SecondaryColor,
                    btn_primary: themeData.ButtonPrimaryColor,
                    btn_secondary: themeData.ButtonTextColor,
                    text_primary: themeData.TextPrimaryColor
                },
                logoUrl: companyLogoUrl,
                IsCustom: 1,
                carWashId: carWashId
            });
        }

        
    }, [themeData,companyLogoUrl]);

        // To get app settings
        const getAppSettings = async () => {
            try {
                setLoading(true); // Show loader
                const { data } =  (cuserType === userType['CARWASH-OWNER']) ? 
                await axios.get(AppsettingshApi.CARWASH_OWNER_GET_APP_SETTINGS) :
                await axios.get(AppsettingshApi.GET_APP_SETTINGS+carWashId);
                if (data.error === 0) {
                    const resthemeData = data?.data?.appTheme ? JSON.parse(data.data.appTheme) : {};
                    if (resthemeData && Object.keys(resthemeData).length > 0)
                    {
                        setThemeData({
                            PrimaryColor: resthemeData.primary,
                            SecondaryColor: resthemeData.secondary,
                            ButtonPrimaryColor: resthemeData.btn_primary,
                            ButtonTextColor: resthemeData.btn_secondary,
                            TextPrimaryColor: resthemeData.text_primary
                        });
                        if(cuserType === userType['CARWASH-OWNER'])
                            {
                                setFormData({
                                    appTheme: {
                                        primary: resthemeData.PrimaryColor,
                                        secondary: resthemeData.SecondaryColor,
                                        btn_primary: resthemeData.ButtonPrimaryColor,
                                        btn_secondary: resthemeData.ButtonTextColor,
                                        text_primary: themeData.TextPrimaryColor
                                    },
                                    logoUrl: data.data.logoFile,
                                    IsCustom: data.data.IsCustom
                                });
                            }
                            else
                            {
                                setFormData({
                                    appTheme: {
                                        primary: resthemeData.PrimaryColor,
                                        secondary: resthemeData.SecondaryColor,
                                        btn_primary: resthemeData.ButtonPrimaryColor,
                                        btn_secondary: resthemeData.ButtonTextColor,
                                        text_primary: themeData.TextPrimaryColor
                                    },
                                    logoUrl: data.data.logoFile,
                                    IsCustom: data.data.IsCustom,
                                    carWashId: carWashId
                                });

                            }
                    }
                    else
                    {
                        setThemeData({
                            PrimaryColor: PRIMARY_COLOR,
                            SecondaryColor: SECONDARY_COLOR,
                            ButtonPrimaryColor: BUTTON_PRIMARY_COLOR,
                            ButtonTextColor: BUTTON_TEXT_COLOR,
                            TextPrimaryColor: TEXT_PRIMARY_COLOR
                        })
                    }
                    const applogourl = data.data.logoFile;
                    setcompanyLogoUrl(applogourl);
                }                
            } catch (error: any) {
                toaster.toastError(error.response?.data?.message || error.message);
            } finally {
                setLoading(false); // Hide loader
            }
        }
      
        // Fetch app settings information on component mount
        useEffect(() => {
        getAppSettings();
        }, []);

        useEffect(() => {
            document.documentElement.style.setProperty('--PrimaryColor', themeData.PrimaryColor);
            document.documentElement.style.setProperty('--SecondaryColor', themeData.SecondaryColor);
            document.documentElement.style.setProperty('--ButtonPrimaryColor', themeData.ButtonPrimaryColor);
            document.documentElement.style.setProperty('--ButtonTextColor',themeData.ButtonTextColor);
            document.documentElement.style.setProperty('--TextPrimaryColor',themeData.TextPrimaryColor);
            }, [themeData]);

  return (
    <>
       
    <div className="settings-container mx-auto">
        <div className="customizer-block mx-auto">
            <div className="title-sec d-flex align-items-center justify-content-center">
                <h5 className='f-bold'>Pick a Color Scheme and Logo</h5>
                <Button variant="light" className="light-btn" onClick={ () => handleShowThemeHelpModal()}>
                    <img src={HelpIcon} alt="Car Wash App" /> 
                </Button>
            </div>
            <div className="theme-element-wrap p-0 border-0">
                <p>Custom Theme</p>
                <div className="color-palette d-flex align-items-center justify-content-center">
                    <div className="color-box d-flex align-items-center justify-content-center position-relative" style={{background: themeData.PrimaryColor}}>
                        <img src={PlusIcon} alt="Car Wash App" /> 
                          <Form.Control
                              type="color"
                              id="CWColorInput1"
                              name="PrimaryColor"
                              className="color-input position-absolute"
                              defaultValue={themeData.PrimaryColor}
                              title="Choose your color"
                              onChange={ColorChangeHandler}
                          />
                    </div>
                    <div className="color-box d-flex align-items-center justify-content-center position-relative" style={{background: themeData.SecondaryColor}}>
                    <img src={PlusIcon} alt="Car Wash App" /> 
                        <Form.Control
                              type="color"
                              id="CWColorInput2"
                              name="SecondaryColor"
                              className="color-input position-absolute"
                              defaultValue={themeData.SecondaryColor}
                              title="Choose your color"
                              onChange={ColorChangeHandler}
                          />
                    </div>
                    <div className="color-box d-flex align-items-center justify-content-center position-relative" style={{background: themeData.ButtonPrimaryColor}}>
                    <img src={PlusIcon} alt="Car Wash App" />
                        <Form.Control
                              type="color"
                              id="CWColorInput3"
                              className="color-input position-absolute"
                              name="ButtonPrimaryColor"
                              defaultValue="#FFD500"
                              title="Choose your color"
                              onChange={ColorChangeHandler}
                          />
                    </div>
                    <div className="color-box d-flex align-items-center justify-content-center position-relative" style={{background: themeData.ButtonTextColor}}>
                    <img src={PlusIcon} alt="Car Wash App" />
                        <Form.Control
                              type="color"
                              id="CWColorInput4"
                              className="color-input position-absolute"
                              name="ButtonTextColor"
                              defaultValue="#172951"
                              title="Choose your color"
                              onChange={ColorChangeHandler}
                          />
                    </div>
                    <div className="color-box d-flex align-items-center justify-content-center position-relative" style={{background: themeData.TextPrimaryColor}}>
                    <img src={PlusIcon} alt="Car Wash App" />
                        <Form.Control
                              type="color"
                              id="CWColorInput5"
                              className="color-input position-absolute"
                              defaultValue="#000000"
                              name="TextPrimaryColor"
                              title="Choose your color"
                              onChange={ColorChangeHandler}
                          />
                    </div>
                </div>
            </div>
            <div className="logo-upload-sec">
                <div className="logo-customize d-flex">
                    <div className="add-logo">
                        <p>Add App Logo</p>
                        <div className="common-cover d-flex align-items-center justify-content-center">
                            <div className="file-upload d-flex align-items-center justify-content-center">
                                <div className="upload-info text-center">
                                    <img src={Browseimgicon} alt="" />
                                    <p className='f-semi'>Browse for Logo Files</p>
                                    {companyLogoName && <span className="file-name">{companyLogoName}</span>}
                                </div>
                                <Form.Control type="file" multiple className='file-input'  onChange={uploadCompanyLogo} ref={fileInputRef} />
                            </div>
                        </div>
                        <p className='note mb-0 f-medium'>Preferred logo resolution is 150- 300 dpi with a transparent background. The file size must not exceed 2 MB.</p>
                    </div>
                    {companyLogo ? (
                        <div className="preview-logo">
                            <p>Logo Preview</p>
                            <div className="common-cover d-flex align-items-center justify-content-center">
                                <Button variant="light" className="light-btn close" onClick={removeCompanyLogo}>
                                    <CloseIcon />
                                </Button>
                                <img src={companyLogo} alt="Car Wash App" height="110px" />
                            </div>
                        </div>
                    ) : (
                        companyLogoUrl && (
                            <div className="preview-logo">
                                <p>Logo Preview</p>
                                <div className="common-cover d-flex align-items-center justify-content-center">
                                    <Button variant="light" className="light-btn close" onClick={removeCompanyLogo}>
                                        <CloseIcon />
                                    </Button>
                                    <img src={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${companyLogoUrl}`} alt="Car Wash App" height="110px" />
                                </div>
                            </div>
                        )
                    )}
                    {logoImageError && (
                        <span className="error-msg">{logoImageError}</span>
                    )}
                </div>
                {/* <Button variant="primary" className="primary-btn f-medium" disabled>Preview</Button> */}
            </div>
        </div>
        <div className="app-preview">
                <p>App Preview</p>
                <div className="app-screens d-flex scroll-custom">
                {companyLogo ?
                    <SplashScreen companyLogo ={companyLogo}/>
                    :
                    <SplashScreen companyLogo ={companyLogoUrl ? `${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${companyLogoUrl}` : companyLogo }/>
                }
                    <HomeScreen/>
                    <SubscriptionsScreen/>
                </div>
            </div>
            {/* <div className='reset-sec text-end'><Button title="Reset" variant="primary" className="secondary-btn" onClick={resetCustomTheme}>Reset</Button></div> */}
            <ThemeHelpModal
                show={showThemeHelpModal} 
                onHide={handleCloseThemeHelpModal} 
            />
    </div>
    {loading && <SmallLoader />}
    </>
  );
};
export default AppCustomizetheme;
